<template>
  <div class="position-relative">
    <b-dropdown
      size="sm"
      :text="dropdownText"
      variant="light"
      class="filtro-dropdown"
    >
      <b-dropdown-item
        v-for="option in options"
        :key="option.label"
        :active="selected?.label === option.label"
        @click="changed(option)"
      >
        <span v-if="option.label === 'Personalizado' && dateRange.length">
          {{ dateRangeFormatted }}
        </span>
        <span v-else>{{ option.label }}</span>
      </b-dropdown-item>
    </b-dropdown>

    <!-- Flatpickr "fantasma" apenas para selecionar o intervalo personalizado -->
    <input
      ref="flatpickrInput"
      type="text"
      class="position-absolute mt-10"
      :class="{ 'd-none': !isCustomPeriod }"
      style="z-index: -1000; color: transparent; font-size: 0px"
    />
  </div>
</template>

<script>
import flatPickr from "flatpickr";
import { Portuguese } from "flatpickr/dist/l10n/pt.js";
import moment from "moment";

export default {
  name: "FiltroPeriodo",
  props: {
    // período controlado pelo pai (opcional): { inicio: 'YYYY-MM-DD', fim: 'YYYY-MM-DD' }
    value: { type: Object, default: () => null },

    // label visual padrão ao montar
    defaultLabel: { type: String, default: "Hoje" },

    // aplica o default no mount quando NÃO houver 'value' vindo do pai
    autoApplyOnMount: { type: Boolean, default: true },
  },

  data() {
    return {
      options: [
        { label: "Hoje" },
        { label: "Ontem" },
        { label: "Últimos 7 dias" },
        { label: "Mês atual" },
        { label: "Mês anterior" },
        { label: "Personalizado" },
      ],
      selected: null,     // { label: string }
      dateRange: [],      // usado apenas em "Personalizado"
      fpInstance: null,
      // eslint-disable-next-line vue/no-reserved-keys
      _lastEmitted: null, // { inicio, fim } para dedupe
    };
  },

  mounted() {
    // Instância do flatpickr: sempre setaremos STRINGS (YYYY-MM-DD)
    this.fpInstance = flatPickr(this.$refs.flatpickrInput, {
      dateFormat: "Y-m-d",
      allowInput: true,
      locale: Portuguese,
      mode: "range",
      defaultDate: this.dateRange, // não usamos Date objects aqui
      appendTo: document.body,
      onClose: (selectedDates) => {
        if (selectedDates.length > 1) {
          this.dateRange = selectedDates;
          this.emitCustomRange();
        }
      },
    });

    // 1) Se o pai já passou um período, detectar preset e ajustar rótulo
    if (this.value?.inicio && this.value?.fim) {
      const preset = this.detectPreset(this.value);
      if (preset) {
        // Mostrar rótulo humano (Hoje, Ontem, etc.)
        this.selected = { label: preset };
        this.dateRange = [];
        this.fpInstance?.setDate([this.value.inicio, this.value.fim], true);
      } else {
        // Qualquer outro período -> "Personalizado"
        this.selected = { label: "Personalizado" };
        this.fpInstance?.setDate([this.value.inicio, this.value.fim], true);
      }
      return;
    }

    // 2) Sem value do pai: usar default (Hoje por padrão)
    const def = this.options.find((o) => o.label === this.defaultLabel) || this.options[0];
    this.selected = def;

    if (this.autoApplyOnMount) {
      const { inicio, fim } = this.computeRange(def.label);
      // opcional: refletir no calendário
      this.fpInstance?.setDate([inicio, fim], true);
      // emitir para o pai
      this.safeEmit({ inicio, fim });
    }
  },

  watch: {
    // Se o pai mudar 'value' depois, sincroniza UI (sem reemitir)
    value: {
      deep: true,
      handler(v) {
        if (!v) {
          // pai limpou -> volta para defaultLabel visualmente
          const def = this.options.find((o) => o.label === this.defaultLabel) || this.options[0];
          this.selected = def;
          this.dateRange = [];
          this.fpInstance?.clear();
          return;
        }

        if (v.inicio && v.fim) {
          const preset = this.detectPreset(v);
          if (preset) {
            this.selected = { label: preset };
            this.dateRange = [];
            this.fpInstance?.setDate([v.inicio, v.fim], true);
          } else {
            this.selected = { label: "Personalizado" };
            this.fpInstance?.setDate([v.inicio, v.fim], true);
          }
        }
      },
    },
  },

  computed: {
    isCustomPeriod() {
      return this.selected?.label === "Personalizado";
    },

    dropdownText() {
      // se for preset, SEMPRE mostra o label
      if (this.selected && this.selected.label !== "Personalizado") {
        return this.selected.label;
      }
      // se for personalizado, mostra o range formatado
      if (this.isCustomPeriod && this.dateRange.length) return this.dateRangeFormatted;
      return this.selected?.label || this.defaultLabel || "Hoje";
    },

    dateRangeFormatted() {
      if (!this.dateRange.length) return "";
      const [d1, d2] = this.dateRange;
      if (!d1 || !d2) return "";

      const format = (d) => moment(d).format("DD/MM/YYYY");
      const a = format(d1);
      const b = format(d2);

      // mesmo dia? mostrar "Hoje" se for hoje; senão só a data
      if (moment(d1).isSame(d2, "day")) {
        if (moment(d1).isSame(moment(), "day")) return "Hoje";
        // opcional: tratar ontem aqui também
        return a;
      }

      return `${a} à ${b}`;
    },
  },

  methods: {
    // Calcula ranges SEMPRE em fuso local
    computeRange(label) {
      const now = moment(); // local
      let inicio, fim;

      switch (label) {
        case "Hoje":
          inicio = now.clone();
          fim    = now.clone();
          break;

        case "Ontem":
          inicio = now.clone().subtract(1, "day");
          fim    = now.clone().subtract(1, "day");
          break;

        case "Últimos 7 dias":
          fim    = now.clone();
          inicio = now.clone().subtract(6, "day");
          break;

        case "Mês atual":
          inicio = now.clone().startOf("month");
          fim    = now.clone(); // até hoje
          break;

        case "Mês anterior":
          inicio = now.clone().subtract(1, "month").startOf("month");
          fim    = now.clone().subtract(1, "month").endOf("month");
          break;

        default:
          return { inicio: null, fim: null };
      }

      return {
        inicio: inicio.format("YYYY-MM-DD"),
        fim:    fim.format("YYYY-MM-DD"),
      };
    },

    // Mapeia presets para ranges atuais e detecta se {inicio,fim} é um deles
    presetRanges() {
      const labels = ["Hoje", "Ontem", "Últimos 7 dias", "Mês atual", "Mês anterior"];
      return labels.map((label) => ({ label, ...this.computeRange(label) }));
    },

    detectPreset({ inicio, fim }) {
      if (!inicio || !fim) return null;
      const hit = this.presetRanges().find((p) => p.inicio === inicio && p.fim === fim);
      return hit ? hit.label : null;
    },

    changed(option) {
      this.selected = option;
      this.handleDatePicker();
    },

    handleDatePicker() {
      const isCustom = this.selected.label === "Personalizado";

      if (!isCustom) {
        // aplicar preset imediatamente
        this.dateRange = [];
        const { inicio, fim } = this.computeRange(this.selected.label);
        // refletir no calendário (strings!)
        this.fpInstance?.setDate([inicio, fim], true);
        this.safeEmit({ inicio, fim });
      }

      // se personalizado, abre o calendário
      this.$nextTick(() => {
        if (isCustom) {
          this.$refs.flatpickrInput.focus();
          setTimeout(() => this.fpInstance?.open(), 0);
        } else {
          this.$refs.flatpickrInput.blur();
        }
      });
    },

    emitCustomRange() {
      if (this.dateRange.length > 1) {
        const [d1, d2] = this.dateRange;
        const inicio = moment(d1).format("YYYY-MM-DD");
        const fim    = moment(d2).format("YYYY-MM-DD");
        this.safeEmit({ inicio, fim });
      }
    },

    // Emite @apply com dedupe para evitar requisições repetidas
    safeEmit({ inicio, fim }) {
      if (!inicio || !fim) return;
      const same =
        this._lastEmitted &&
        this._lastEmitted.inicio === inicio &&
        this._lastEmitted.fim === fim;
      if (same) return;
      this._lastEmitted = { inicio, fim };
      this.$emit("apply", { inicio, fim });
    },
  },
};
</script>

<style>
.filtro-dropdown .dropdown-toggle {
  color: var(--primary-color);
}
</style>
